import { cn } from "@/shared/utils/classname-merger";
import { Button } from "@nextui-org/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NavItemData, NavItemChild } from "../../types/sidebar";
import { useLoggedInOrganization } from "@/modules/auth/states";
import { useActiveSidebarSection } from "../../hooks/useActiveSidebarSection";

const SIDEBAR_CHILD_STORAGE_PREFIX = "sidebar-active-child:";

interface AccordionMenuItemProps {
  data: NavItemData;
  isCollapsed: boolean;
  onExpandSidebar?: () => void;
}

const ChildNavItem = ({
  child,
  isActive,
  onClick,
}: {
  child: NavItemChild;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left text-[14px] leading-5 h-nav-child",
        "py-2 pr-2",
        "pl-[17px]",
        "transition-colors duration-150",
        "font-normal text-[#71717A]",
        "hover:bg-[#F8FAFC] hover:text-[#11181C]",
        isActive && [
          "bg-[#F8FAFC]",
          "text-[#11181C] font-medium",
          "border-l-[2px] border-[#006FEE]",
          "ml-[-1px]",
          "rounded-r-[4px]",
        ],
      )}
    >
      {child.title}
    </button>
  );
};

export const AccordionMenuItem = ({
  data,
  isCollapsed,
  onExpandSidebar,
}: AccordionMenuItemProps) => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { title, Icon, path, children } = data;

  // Get organization context for child visibility
  const loggedInOrganization = useLoggedInOrganization();
  const orgName = loggedInOrganization?.organization_name;
  const { section: activeSidebarSection, childPath: activeChildPath } =
    useActiveSidebarSection();

  // Filter children based on canAccess function
  const visibleChildren = children?.filter((child) => {
    if (!child.canAccess) return true;
    return child.canAccess(orgName);
  });

  // Extract base path (e.g., "third-party-risk" from "third-party-risk/home")
  const basePath = path.split("/")[0];

  // Check if current route matches parent base path OR any visible child path
  const isActive =
    pathname.startsWith("/" + basePath) ||
    visibleChildren?.some((child) => pathname.includes(child.path)) ||
    activeSidebarSection === basePath ||
    false;

  // Find which visible child matches the current pathname
  const pathnameChildIndex =
    visibleChildren?.findIndex((child) => pathname.includes(child.path)) ?? -1;

  // Store the active child in sessionStorage whenever one matches by pathname
  useEffect(() => {
    if (pathnameChildIndex >= 0 && visibleChildren) {
      sessionStorage.setItem(
        `${SIDEBAR_CHILD_STORAGE_PREFIX}${basePath}`,
        visibleChildren[pathnameChildIndex].path,
      );
    }
  }, [pathnameChildIndex, basePath, visibleChildren]);

  // Resolve activeChildIndex with 3-level fallback
  const activeChildIndex = useMemo(() => {
    // 1. Direct pathname match (always correct)
    if (pathnameChildIndex >= 0) return pathnameChildIndex;
    if (!isActive || !visibleChildren) return -1;

    // 2. Hook's childPath (unambiguous agent types + deep routes)
    if (activeChildPath) {
      const idx = visibleChildren.findIndex((c) => c.path === activeChildPath);
      if (idx >= 0) return idx;
    }

    // 3. sessionStorage fallback (remembers last visited child in this section)
    const stored = sessionStorage.getItem(
      `${SIDEBAR_CHILD_STORAGE_PREFIX}${basePath}`,
    );
    if (stored) {
      const idx = visibleChildren.findIndex((c) => c.path === stored);
      if (idx >= 0) return idx;
    }

    return -1;
  }, [pathnameChildIndex, isActive, visibleChildren, activeChildPath, basePath]);

  // Auto-expand when route matches
  const [isExpanded, setIsExpanded] = useState(isActive);

  // Update expanded state when route changes
  useEffect(() => {
    if (isActive) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [isActive]);

  const handleParentClick = () => {
    if (isCollapsed) {
      // In collapsed mode, navigate to first visible child and expand sidebar
      if (visibleChildren && visibleChildren.length > 0) {
        navigate(visibleChildren[0].path);
      } else {
        navigate(path);
      }
      onExpandSidebar?.();
    } else {
      // In expanded mode, toggle accordion
      if (!isExpanded && visibleChildren && visibleChildren.length > 0) {
        // When expanding, navigate to first visible child
        navigate(visibleChildren[0].path);
      }
      setIsExpanded(!isExpanded);
    }
  };

  const handleChildClick = (child: NavItemChild) => {
    navigate(child.path);
  };

  // Collapsed mode: show icon only
  if (isCollapsed) {
    return (
      <Button
        onClick={handleParentClick}
        isIconOnly
        className={cn(
          "h-fit w-fit font-normal text-text-secondary bg-transparent rounded-[12px] px-[16px] py-[10px]",
          isActive && "text-brand-primary",
        )}
      >
        <Icon
          color={isActive ? "#006FEE" : "#62748E"}
          strokeWidth={isActive ? 2 : 1.5}
          className="min-h-[20px] min-w-[20px] h-[20px] w-[20px]"
        />
      </Button>
    );
  }

  // Expanded mode: show accordion with children
  return (
    <div className="flex flex-col">
      {/* Parent row */}
      <button
        onClick={handleParentClick}
        className={cn(
          "flex items-center w-full",
          "h-nav-item p-nav-item gap-nav-gap",
          "transition-colors duration-150",
          "text-text-secondary font-normal",
          isActive && "text-brand-primary font-medium",
        )}
      >
        <Icon
          color={isActive ? "#006FEE" : "#62748E"}
          strokeWidth={isActive ? 2 : 1.5}
          className="min-h-[20px] min-w-[20px] h-[20px] w-[20px]"
        />
        <span className="text-[14px] leading-5">{title}</span>
      </button>

      {/* Children container with animation */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-col mt-1 pl-[22px]">
              <div className="border-l border-[#E4E4E7] flex flex-col">
                {visibleChildren?.map((child, index) => (
                  <ChildNavItem
                    key={child.title}
                    child={child}
                    isActive={activeChildIndex === index}
                    onClick={() => handleChildClick(child)}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccordionMenuItem;
